import React from 'react';
import { useTranslation } from 'react-i18next';
import { LICENSE_TYPE_KEY_BY_VALUE } from '../../utils/constants';

const isMilitaryLicense = (l) =>
  typeof l.type === 'string' && l.type.startsWith('Military/Law Enforcement');

const yesNoKey = (v) => (v ? 'options.yes' : 'options.no');

const ApplicationReview = ({
  values = {},
  licenses,
  training,
  languages,
  martialArts,
  files,
  onEdit
}) => {
  const { t } = useTranslation();

  const show = (v) => {
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string' && v.trim()) return v;
    return t('review.empty');
  };

  const yesNo = (v) => t(yesNoKey(v));

  // A license type is either one of the canonical LICENSE_TYPES values (which
  // have a translation) or free text the applicant typed under "Other".
  const resolveLicenseType = (l) => {
    if (l.type === 'other') return l.otherType || t('options.licenseTypes.other');
    const key = LICENSE_TYPE_KEY_BY_VALUE[l.type];
    return key ? t(`options.licenseTypes.${key}`) : l.type;
  };

  const resolveAuthority = (l) =>
    l.issuingAuthority === 'other'
      ? (l.otherAuthority || t('options.licenseTypes.other'))
      : l.issuingAuthority;

  const resolveLanguage = (l) =>
    l.language === 'other'
      ? (l.otherLanguage || t('options.spokenLanguages.other'))
      : t(`options.spokenLanguages.${l.language}`, { defaultValue: l.language });

  const licenseLine = (l) => {
    if (isMilitaryLicense(l)) return resolveLicenseType(l);
    return t('review.licenseLine', {
      type: resolveLicenseType(l),
      number: l.number,
      authority: resolveAuthority(l),
      expiry: show(l.expiryDate)
    });
  };

  const Row = ({ label, value }) => (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{show(value)}</dd>
    </div>
  );

  const Section = ({ title, step, children }) => (
    <div className="border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <button
          type="button"
          onClick={() => onEdit(step)}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          aria-label={t('review.editSection', { section: title })}
        >
          {t('review.edit')}
        </button>
      </div>
      {children}
    </div>
  );

  const SubHeading = ({ children }) => (
    <h4 className="text-sm font-medium text-gray-700 mt-3 mb-1">{children}</h4>
  );

  const BulletList = ({ items, emptyText }) =>
    items.length ? (
      <ul className="text-sm text-gray-900 list-disc ml-5 space-y-1">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-gray-500">{emptyText}</p>
    );

  const asArray = (a) => (Array.isArray(a) ? a : []);
  const completeLicenses = asArray(licenses).filter(
    (l) => l.type && (l.number || isMilitaryLicense(l))
  );
  const completeTraining = asArray(training).filter((tr) => tr.courseName);
  const spokenLanguages = asArray(languages).filter((l) => l.language);
  const arts = values.skills?.martialArtsTrained
    ? asArray(martialArts).filter((m) => m.style)
    : [];

  const address = values.personalInfo?.address || {};
  const skills = values.skills || {};
  const driving = skills.drivingLicense || {};
  const firstAid = skills.firstAid || {};
  const weapons = skills.weaponsTraining || {};
  const background = values.backgroundCheck || {};
  const military = values.personalInfo?.militaryService || {};
  const availability = values.availability || {};
  const insuranceAnswer = values.insurance?.hasLiabilityInsurance;

  const listSeparator = t('review.listSeparator', { defaultValue: ', ' });

  const workType = [
    availability.fullTime && t('availability.fullTime'),
    availability.partTime && t('availability.partTime')
  ].filter(Boolean).join(listSeparator);

  const alsoAvailable = [
    availability.nightShifts && t('availability.nightShifts'),
    availability.weekends && t('availability.weekends'),
    availability.willingToTravel && t('availability.willingToTravel')
  ].filter(Boolean).join(listSeparator);

  const documents = [
    `${t('review.documentLabels.id')}: ${show(files?.id?.name)}`,
    ...asArray(files?.license).map((f) => `${t('review.documentLabels.license')}: ${f.name}`),
    ...asArray(files?.certification).map((f) => `${t('review.documentLabels.certification')}: ${f.name}`),
    ...asArray(files?.other).map((f) => `${t('review.documentLabels.other')}: ${f.name}`)
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900">{t('review.heading')}</h2>
      <p className="text-sm text-gray-500 mb-6">{t('review.help')}</p>

      <Section title={t('review.contactTitle')} step={1}>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          <Row label={t('review.labels.firstName')} value={values.firstName} />
          <Row label={t('review.labels.lastName')} value={values.lastName} />
          <Row label={t('review.labels.email')} value={values.email} />
          <Row label={t('review.labels.phone')} value={values.phone} />
          <Row label={t('review.labels.street')} value={address.street} />
          <Row label={t('review.labels.city')} value={address.city} />
          <Row label={t('review.labels.state')} value={address.state} />
          <Row label={t('review.labels.zip')} value={address.zip} />
          <Row label={t('review.labels.dateOfBirth')} value={values.personalInfo?.dateOfBirth} />
          <Row
            label={t('review.labels.gender')}
            value={
              values.physicalAttributes?.gender
                ? t(`options.gender.${values.physicalAttributes.gender}`)
                : undefined
            }
          />
          <Row label={t('review.labels.referralCode')} value={(values.referralCode || '').toUpperCase()} />
        </dl>
      </Section>

      <Section title={t('review.licensesTitle')} step={2}>
        <BulletList
          items={completeLicenses.map(licenseLine)}
          emptyText={t('review.noLicenses')}
        />
        <SubHeading>{t('review.trainingTitle')}</SubHeading>
        <BulletList
          items={completeTraining.map(
            (tr) => `${tr.courseName}${tr.institution ? `, ${tr.institution}` : ''}${tr.completionDate ? ` (${tr.completionDate})` : ''}`
          )}
          emptyText={t('review.noTraining')}
        />
      </Section>

      <Section title={t('review.skillsTitle')} step={3}>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          <Row label={t('review.labels.yearsOfExperience')} value={values.experience?.yearsOfExperience} />
          <Row label={t('review.labels.specializations')} value={values.experience?.specializations} />
          <Row
            label={t('review.labels.languages')}
            value={spokenLanguages
              .map((l) => `${resolveLanguage(l)} (${t(`options.proficiency.${l.proficiency}`, { defaultValue: l.proficiency })})`)
              .join(listSeparator)}
          />
          <Row
            label={t('review.labels.firstAid')}
            value={firstAid.certified
              ? `${t('options.yes')}${firstAid.provider ? ` (${firstAid.provider})` : ''}`
              : t('options.no')}
          />
          <Row
            label={t('review.labels.weapons')}
            value={weapons.trained
              ? `${t('options.yes')}${weapons.permitNumber ? ` (${t('review.permitNumber', { number: weapons.permitNumber })})` : ''}`
              : t('options.no')}
          />
          <Row
            label={t('review.labels.military')}
            value={
              military.served
                ? `${t('options.yes')}${military.branch ? ` (${military.branch}${military.rank ? `, ${military.rank}` : ''})` : ''}`
                : t('options.no')
            }
          />
          <Row
            label={t('review.labels.martialArts')}
            value={
              skills.martialArtsTrained
                ? (arts.map((m) => `${m.style}${m.level ? ` (${m.level})` : ''}`).join(listSeparator) || t('options.yes'))
                : t('options.no')
            }
          />
          <Row
            label={t('review.labels.drivingLicense')}
            value={yesNo(driving.hasLicense) + (driving.defensiveDriving ? t('review.defensiveDrivingSuffix') : '')}
          />
          <Row label={t('review.labels.consent')} value={yesNo(background.consent)} />
          <Row label={t('review.labels.criminalRecord')} value={yesNo(background.criminalRecord?.hasCriminalRecord)} />
          <Row
            label={t('review.labels.insurance')}
            value={insuranceAnswer === 'yes' ? t('options.yes') : insuranceAnswer === 'no' ? t('options.no') : undefined}
          />
          <Row label={t('review.labels.availableFrom')} value={availability.startDate} />
          <Row label={t('review.labels.workType')} value={workType} />
          <Row label={t('review.labels.alsoAvailable')} value={alsoAvailable} />
        </dl>
        {(values.additionalInfo || '').trim() && (
          <>
            <SubHeading>{t('review.additionalInfoTitle')}</SubHeading>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{values.additionalInfo}</p>
          </>
        )}
      </Section>

      <Section title={t('review.documentsTitle')} step={4}>
        <BulletList items={documents} emptyText={t('review.noDocuments')} />
      </Section>
    </div>
  );
};

export default ApplicationReview;
